import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Target, TrendingUp, Flame, Check } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api_getMyProfile } from "@/lib/api";
import Logo from "@/components/Logo";

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const existing = !!user;
  const goNext = () => navigate(user ? (user.has_profile ? "/home" : "/onboarding") : "/login");
  const [streak, setStreak] = useState(0);

  useEffect(() => {
    if (user?.has_profile) {
      api_getMyProfile()
        .then((p) => setStreak(p.streak_count || 0))
        .catch(() => setStreak(0));
    } else {
      setStreak(0);
    }
  }, [user]);

  const heroImg =
    "https://images.unsplash.com/photo-1526323610139-381ca0f77a12?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85&fit=crop";

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Header */}
      <header className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <Logo size={36} />
          <span className="font-heading font-black tracking-tight text-lg">Fairway Lab</span>
        </div>
        <button
          onClick={goNext}
          data-testid="landing-cta-top"
          className="btn-ghost text-sm py-2 px-4"
        >
          {existing ? "Go to practice" : "Start free"}
        </button>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-10 sm:pt-20 pb-16 relative">
        <div className="grid lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-3">
            <span className="chip mb-6" data-testid="hero-chip">
              <Target className="w-3 h-3 text-[#0B2545]" /> Guided Practice · All Handicaps
            </span>
            <h1 className="h-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] text-slate-900">
              Know exactly <br />
              <span className="text-[#0B2545]">what to practice</span> <br />
              today — and why.
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-slate-600 font-medium max-w-xl leading-relaxed">
              Fairway Lab is the decision engine for your practice. Tell us your handicap, frustrations, and time —
              we'll build a structured session with drills, targets, and success criteria. Practice with intent.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button
                onClick={goNext}
                data-testid="landing-cta-primary"
                className="btn-primary"
              >
                {existing ? "Continue your journey" : "Build my first session"}
                <ArrowRight className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-500 sm:self-center px-3">
                <Check className="w-4 h-4 text-emerald-500" /> No signup. Try in 60 seconds.
              </div>
            </div>

            {/* feature bullets */}
            <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-4 stagger">
              {[
                { icon: Target, title: "Personalized", desc: "Drills matched to your handicap and misses." },
                { icon: TrendingUp, title: "Adaptive", desc: "Sessions evolve based on your actual results." },
                { icon: Flame, title: "Motivating", desc: "Streaks and progress that keep you showing up." },
              ].map((f) => (
                <div key={f.title} className="bg-slate-50 border border-slate-200 rounded-2xl p-5">
                  <f.icon className="w-5 h-5 text-[#0B2545]" />
                  <h4 className="font-heading font-bold text-lg mt-3">{f.title}</h4>
                  <p className="text-sm text-slate-600 mt-1 leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-2 relative">
            <button
              type="button"
              onClick={goNext}
              data-testid="landing-todays-session-card"
              className="block w-full text-left rounded-3xl overflow-hidden aspect-[3/4] border border-slate-200 shadow-xl relative group focus:outline-none focus:ring-4 focus:ring-[#0B2545]/20 transition-transform hover:-translate-y-1"
            >
              <img src={heroImg} alt="Bucket of practice balls on country-club turf" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-5 border border-white/50 flex items-center justify-between gap-3">
                  <p className="font-heading font-bold text-lg leading-tight text-slate-900">
                    Get started on today's session
                  </p>
                  <span className="shrink-0 w-10 h-10 rounded-full bg-[#0B2545] text-white flex items-center justify-center transition-transform group-hover:translate-x-1">
                    <ArrowRight className="w-5 h-5" />
                  </span>
                </div>
              </div>
            </button>
            {/* Floating stat */}
            <div className="hidden md:flex absolute -left-8 top-10 bg-white border border-slate-200 rounded-2xl p-4 shadow-lg items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#0B2545] text-white flex items-center justify-center">
                <Flame className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-500 font-bold">Streak</p>
                <p className="font-heading font-black text-2xl leading-none" data-testid="landing-streak">{streak} {streak === 1 ? "day" : "days"}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-20">
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Why Fairway Lab</p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-heading font-black tracking-tight">
              Most golfers practice a lot — but not on purpose.
            </h2>
            <p className="mt-4 text-slate-600 font-medium leading-relaxed">
              Drill libraries are everywhere. What's missing is a coach telling you <em>do this today, for this reason, to fix this problem.</em>
              That's all we do.
            </p>
          </div>

          <div className="mt-10 grid md:grid-cols-2 gap-4">
            {[
              { title: "Range → Course translation", desc: "We forecast how today's reps should move your score on Saturday." },
              { title: "Adaptive difficulty", desc: "Success windows shrink as you get better. Your sessions keep up." },
              { title: "Honest feedback loop", desc: "Tap success / mixed / failed after each drill. We adjust next time." },
              { title: "Flexible time blocks", desc: "Got 15 minutes? We have a session. 60? We have one too." },
            ].map((b) => (
              <div key={b.title} className="bg-white border border-slate-200 rounded-2xl p-6">
                <h4 className="font-heading font-bold text-lg">{b.title}</h4>
                <p className="mt-2 text-slate-600 text-sm leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="max-w-6xl mx-auto px-4 sm:px-6 py-12 pb-32 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-sm text-slate-500">
        <p className="font-medium">© Fairway Lab — Practice with intent.</p>
        <button onClick={goNext} data-testid="landing-cta-footer" className="btn-primary text-sm py-2 px-5">
          Start free <ArrowRight className="w-4 h-4" />
        </button>
      </footer>
    </div>
  );
}
